import React, { useEffect, useState } from "react";
import { Table, Spinner, Badge } from "react-bootstrap";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { WEEK_META } from "../utils/guidanceWeeks";

const API = process.env.REACT_APP_API;

// รายละเอียดของห้องหนึ่ง เมื่อกางแถวในหน้าติดตามคาบแนะแนว (อ่านอย่างเดียว)
//   weeks    — ไล่รายสัปดาห์ว่าเช็ค/ขาด/งดตามปฏิทิน (มากับข้อมูลภาพรวม ไม่ต้องยิง API)
//   sessions — กิจกรรมแนะแนวรายครั้ง (หัวข้อ + ผลการเข้าร่วม)
//   students — สรุปการเข้าคาบแนะแนวรายนักเรียน
export default function GuidanceRoomDetail({ tab, rId, yearId, semester, weeks }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (tab === "weeks") { setLoading(false); return; } // ใช้ข้อมูลที่โหลดมาแล้ว
    setLoading(true);
    const endpoint = tab === "students" ? "getGuidanceStudentsByRoom" : "getGuidanceSessionsByRoom";
    axiosInstance
      .get(`${API}/${endpoint}/${rId}/${yearId}/${semester}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (active) setRows(res.data || []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tab, rId, yearId, semester]);

  if (tab === "weeks") {
    if (!weeks || weeks.length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <i className="ri-inbox-line fs-4 d-block mb-1"></i>
          ยังไม่มีกรอบสัปดาห์ของภาคเรียนนี้
        </div>
      );
    }
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 120 }}>วันที่</th>
            <th style={{ width: 140 }}>สถานะ</th>
            <th>เหตุผล / หัวข้อ</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.date}>
              <td className="fs-sm">{w.date}</td>
              <td>
                <Badge bg={WEEK_META[w.status].bg}
                  className={`fs-xs${w.status === "upcoming" ? " text-dark" : ""}`}>
                  {WEEK_META[w.status].label}
                </Badge>
              </td>
              <td className="fs-sm">
                {w.status === "no_teaching" && (
                  <span><i className="ri-calendar-close-line me-1 text-muted"></i>{w.reason}</span>
                )}
                {w.status === "checked" && (
                  <>
                    <span>{w.topic || <span className="text-muted">ไม่ระบุหัวข้อ</span>}</span>
                    {w.reason && (
                      <span className="fs-xs text-muted d-block">
                        (ปฏิทินระบุงดการเรียนการสอน: {w.reason} — แต่ห้องนี้เช็คชื่อไว้)
                      </span>
                    )}
                  </>
                )}
                {w.status === "missed" && (
                  <span className="text-danger">ไม่มีการเช็คชื่อ และปฏิทินไม่ได้ระบุว่างดการเรียนการสอน</span>
                )}
                {w.status === "upcoming" && <span className="text-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  if (loading) {
    return (
      <div className="text-center text-muted py-3">
        <Spinner animation="border" size="sm" /> กำลังโหลด...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <i className="ri-inbox-line fs-4 d-block mb-1"></i>
        {tab === "students" ? "ไม่มีนักเรียนในห้องนี้" : "ยังไม่มีการเช็คชื่อคาบแนะแนวในภาคเรียนนี้"}
      </div>
    );
  }

  if (tab === "students") {
    return (
      <Table size="sm" hover responsive className="mb-0 align-middle">
        <thead className="table-secondary">
          <tr>
            <th style={{ width: 110 }}>เลขประจำตัว</th>
            <th>ชื่อ-สกุล</th>
            <th className="text-center" style={{ width: 70 }}>ทั้งหมด</th>
            <th className="text-center" style={{ width: 60 }}>มา</th>
            <th className="text-center" style={{ width: 60 }}>ขาด</th>
            <th className="text-center" style={{ width: 60 }}>สาย</th>
            <th className="text-center" style={{ width: 60 }}>ลา</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.st_idnumber}>
              <td className="fs-sm">{r.st_idnumber}</td>
              <td className="fs-sm">{r.st_name}</td>
              <td className="text-center">{Number(r.total_sessions || 0)}</td>
              <td className="text-center text-success">{Number(r.presents || 0)}</td>
              <td className="text-center">
                {Number(r.absents) > 0
                  ? <Badge bg="danger">{r.absents}</Badge>
                  : <span className="text-muted">0</span>}
              </td>
              <td className="text-center text-warning">{Number(r.lates || 0)}</td>
              <td className="text-center text-info">{Number(r.leaves || 0)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <Table size="sm" hover responsive className="mb-0 align-middle">
      <thead className="table-secondary">
        <tr>
          <th style={{ width: 150 }}>วันที่</th>
          <th style={{ minWidth: 180 }}>หัวข้อกิจกรรม</th>
          <th style={{ minWidth: 120 }}>ผู้เช็ค</th>
          <th className="text-center" style={{ width: 60 }}>นร.</th>
          <th className="text-center" style={{ width: 60 }}>มา</th>
          <th className="text-center" style={{ width: 60 }}>ขาด</th>
          <th className="text-center" style={{ width: 60 }}>สาย</th>
          <th className="text-center" style={{ width: 60 }}>ลา</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.gtr_id}>
            <td className="fs-sm">
              {r.att_day_th ? `${r.att_day_th} ` : ""}{r.att_date_th || r.att_date}
            </td>
            <td className="fs-sm">{r.gtr_topic || <span className="text-muted">ไม่ระบุหัวข้อ</span>}</td>
            <td className="fs-sm">{r.checked_by_name || <span className="text-muted">-</span>}</td>
            <td className="text-center">{Number(r.total_students || 0)}</td>
            <td className="text-center text-success">{Number(r.presents || 0)}</td>
            <td className="text-center text-danger">{Number(r.absents || 0)}</td>
            <td className="text-center text-warning">{Number(r.lates || 0)}</td>
            <td className="text-center text-info">{Number(r.leaves || 0)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
